import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip
} from '@mui/material';
import {
  Public,
  Assignment,
  Speed,
  Security,
  CheckCircle
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const Home = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const features = [
    {
      icon: <Public />,
      title: 'Global Coverage',
      description: 'Assessment for 20+ countries including Canada, USA, Australia, Germany, and more'
    },
    {
      icon: <Assignment />,
      title: 'Step-by-Step Forms',
      description: 'Easy-to-complete forms guide you through gathering all necessary information'
    },
    {
      icon: <Speed />,
      title: 'AI-Powered Matching',
      description: 'Advanced algorithms analyze your profile against immigration requirements'
    },
    {
      icon: <Security />,
      title: 'Secure & Private',
      description: 'Your data is encrypted and never shared without your explicit consent'
    }
  ];

  const countries = [
    'Canada', 'Australia', 'USA', 'Germany', 'New Zealand', 'UK'
  ];

  return (
    <Container maxWidth="lg">
      {/* Hero Section */}
      <Box
        sx={{
          textAlign: 'center',
          py: { xs: 6, md: 10 },
          mb: 6
        }}
      >
        <Typography
          variant="h2"
          component="h1"
          sx={{
            fontWeight: 700,
            mb: 3,
            fontSize: { xs: '2.5rem', md: '3.5rem' }
          }}
        >
          Your Immigration Journey Starts Here
        </Typography>
        
        <Typography
          variant="h5"
          sx={{
            mb: 4,
            color: 'text.secondary',
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          Complete a simple step-by-step form to assess your immigration options across 20+ countries 
          and auto-fill application forms with AI assistance
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          {isAuthenticated() ? (
            <>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/dashboard')}
                sx={{ px: 4, py: 1.5 }}
              >
                Go to Dashboard
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/onboarding')}
                sx={{ px: 4, py: 1.5 }}
              >
                Start Assessment
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/register')}
                sx={{ px: 4, py: 1.5 }}
              >
                Get Started Free
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/login')}
                sx={{ px: 4, py: 1.5 }}
              >
                Sign In
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Features Section */}
      <Box sx={{ mb: 8 }}>
        <Typography
          variant="h4"
          component="h2"
          sx={{ textAlign: 'center', mb: 6, fontWeight: 600 }}
        >
          Why Choose VisaForge?
        </Typography>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card sx={{ height: '100%', p: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: 'primary.main', 
                        mr: 2,
                        width: 56,
                        height: 56
                      }}
                    >
                      {feature.icon}
                    </Avatar>
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                      {feature.title}
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Countries Section */}
      <Box sx={{ mb: 8 }}>
        <Typography
          variant="h4"
          component="h2"
          sx={{ textAlign: 'center', mb: 4, fontWeight: 600 }}
        >
          Supported Countries
        </Typography>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2 }}>
          {countries.map((country) => (
            <Chip
              key={country}
              label={country}
              variant="outlined"
              size="large"
              sx={{ 
                fontSize: '1rem',
                py: 2,
                px: 1
              }}
            />
          ))}
          <Chip
            label="+ 14 more"
            variant="filled"
            color="primary"
            size="large"
            sx={{ 
              fontSize: '1rem',
              py: 2,
              px: 1
            }}
          />
        </Box>
      </Box>

      {/* How it Works */}
      <Box sx={{ mb: 8 }}>
        <Typography
          variant="h4"
          component="h2"
          sx={{ textAlign: 'center', mb: 6, fontWeight: 600 }}
        >
          How It Works
        </Typography>

        <Grid container spacing={4}>
          {[
            {
              step: '1',
              title: 'Complete Your Profile',
              description: 'Fill out step-by-step forms to build your comprehensive immigration profile'
            },
            {
              step: '2',
              title: 'Get Matched',
              description: 'Receive personalized recommendations with success probabilities'
            },
            {
              step: '3',
              title: 'Generate Forms',
              description: 'Auto-fill immigration forms based on your profile data'
            }
          ].map((item, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Box sx={{ textAlign: 'center' }}>
                <Avatar
                  sx={{
                    bgcolor: 'primary.main',
                    width: 64,
                    height: 64,
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    mx: 'auto',
                    mb: 2
                  }}
                >
                  {item.step}
                </Avatar>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  {item.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {item.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          textAlign: 'center',
          py: 6,
          px: 4,
          bgcolor: 'primary.main',
          color: 'white',
          borderRadius: 2,
          mb: 6
        }}
      >
        <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
          Ready to Start Your Immigration Journey?
        </Typography>
        <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
          Join thousands who have successfully assessed their immigration options
        </Typography>
        {!isAuthenticated() && (
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/register')}
            sx={{
              bgcolor: 'white',
              color: 'primary.main',
              px: 4,
              py: 1.5,
              '&:hover': {
                bgcolor: 'grey.100'
              }
            }}
          >
            Start Free Assessment
          </Button>
        )}
      </Box>
    </Container>
  );
};

export default Home;